/**
 * Puente de sesión app móvil → web: la app nativa inyecta access/refresh token
 * y esta página llama a supabase.auth.setSession y recarga a la ruta destino.
 * Ruta pública (sin ProtectedRoute).
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

declare global {
  interface Window {
    __clienteloApplyMobileSession?: (p: MobileSessionPayload) => void;
    __CLIENTELO_PENDING_MOBILE_SESSION?: MobileSessionPayload;
  }
}

export type MobileSessionPayload = {
  access_token: string;
  refresh_token: string;
  /** Path + query on same origin, e.g. /configuracion?tab=entrega */
  next?: string;
};

export const MobileConfigBridge: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const appliedRef = useRef(false);

  useLayoutEffect(() => {
    const apply = async (p: MobileSessionPayload) => {
      if (appliedRef.current) return;
      appliedRef.current = true;
      setError(null);

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: p.access_token,
        refresh_token: p.refresh_token,
      });

      if (sessionError) {
        appliedRef.current = false;
        setError(sessionError.message || 'No se pudo iniciar sesión en la web.');
        return;
      }

      const raw = p.next && p.next.startsWith('/') ? p.next : '/configuracion';
      window.location.replace(`${window.location.origin}${raw}`);
    };

    window.__clienteloApplyMobileSession = apply;

    const pending = window.__CLIENTELO_PENDING_MOBILE_SESSION;
    if (pending) {
      delete window.__CLIENTELO_PENDING_MOBILE_SESSION;
      void apply(pending);
    }

    return () => {
      delete window.__clienteloApplyMobileSession;
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      <p className="mt-6 text-sm text-gray-600 text-center">
        {error ? error : 'Conectando con tu cuenta…'}
      </p>
      {error ? (
        <a href="/login" className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800">
          Ir a iniciar sesión
        </a>
      ) : null}
    </div>
  );
};
